import { DealsTable } from '../../../components/Tables/DealsTable/DealsTable'
import React from 'react'
import { formatInteger } from '../../../utils/formatting'
import { DEAL_TABLE_KEY } from 'constants/tableQueryKeys'
import useApiService from 'services/api'
import {
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { FBPage } from '../../../components/FbUI/Page/FBPage'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { MetricCard } from 'components/FbUI/MetricCard'
import { PiHandshake, PiTable, PiKanban, PiGearSix } from 'react-icons/pi'
import { PillTabs, TabPanel } from 'components/FbUI/PillTabs'
import { DealsKanbanBoard } from 'components/Deals/DealsKanbanBoard'
import { Link } from 'react-router-dom'
import useCachedState from 'utils/useCachedState'

type DealsView = 'table' | 'board'

/** Persist the rep's Table/Board choice across navigation (e.g. opening a deal
 *  and coming back) and reloads. */
const DEALS_VIEW_STORAGE_KEY = 'deals-view-mode'
const isDealsView = (v: unknown): v is DealsView =>
  v === 'table' || v === 'board'

const DEALS_VIEW_TABS: ReadonlyArray<{
  key: DealsView
  title: React.ReactNode
}> = [
  {
    key: 'table',
    title: (
      <span className="flex items-center gap-1.5">
        <PiTable size={16} />
        Table
      </span>
    ),
  },
  {
    key: 'board',
    title: (
      <span className="flex items-center gap-1.5">
        <PiKanban size={16} />
        Board
      </span>
    ),
  },
]

export function DealsPage() {
  const [view, setView] = useCachedState<DealsView>(
    'table',
    DEALS_VIEW_STORAGE_KEY,
    isDealsView
  )
  // `dealDetailPage` gates the pre-existing Deals Table V2 column set;
  // `dealKanbanView` independently gates this PR's Table/Board toggle + Kanban
  // board, so the board can roll out separately from the V2 table audience.
  const { dealDetailPage, dealKanbanView } = useFeatureFlagService()
  const api = useApiService()

  // Header metric is the grand total of deals, independent of which view is
  // active and of any applied filters. Comes from the report-deals read-model
  // via a tiny page_size:1 fetch.
  const { data: totalResp } = api.useGetReportDeals({ page_size: 1 })
  const totalDeals = totalResp?.count
  const dealsMetric = totalDeals == null ? '—' : formatInteger(totalDeals)

  const defaultColumnVisibility = dealDetailPage
    ? {
        deal: true,
        identifier: false,
        deal_name: false,
        predicted_annual_revenue: false,
        predicted_monthly_revenue: false,
        monthly_revenue_override: false,
      }
    : {
        identifier: false,
        predicted_annual_revenue: false,
        predicted_monthly_revenue: false,
        monthly_revenue_override: false,
      }

  const tableContent = (
    <DealsTable
      useReports={true}
      tableKey={DEAL_TABLE_KEY}
      defaultColumnVisibility={defaultColumnVisibility}
      showCompanyColumn
    />
  )

  return (
    <FBPage
      innerPageClassName="!bg-[#fefefe]"
      containerClassName="!bg-[#fefefe] !shadow-none"
    >
      <FBPageHeader>
        <FBPageTitleRow>
          <div className="flex flex-col">
            {/* Eyebrow badge signals the section (mirrors the Contacts page),
                freeing the title below to describe what this view actually
                does now that there's a board alongside the table. */}
            <span className="mb-2 inline-flex items-center gap-1.5 self-start rounded-full bg-neutral-100 px-2.5 py-0.5 text-sm font-medium tracking-tight text-neutral-700">
              <PiHandshake size={14} />
              Deals
            </span>
            <FBPageTitle className="!text-fg">Sales pipeline</FBPageTitle>
            <p className="mt-1 max-w-2xl text-sm text-fg-secondary">
              Track every opportunity from first conversation to closed-won.
              Switch between the table and board view to work the way that suits
              you.
            </p>
          </div>
          <div className="ml-auto">
            <MetricCard
              icon={<PiHandshake size={18} />}
              label="Total Deals"
              value={dealsMetric}
            />
          </div>
        </FBPageTitleRow>
      </FBPageHeader>
      {dealKanbanView && (
        <div className="mb-3 flex items-center gap-3">
          <PillTabs
            tabs={DEALS_VIEW_TABS}
            activeKey={view}
            onChange={setView}
            idPrefix="deals-view"
            aria-label="Deals view"
            className="w-fit"
          />
          {/* DS2026 bright-green text link, aligned with the view tabs, into the
              Sales Stages settings table — the source of truth for the stages
              that become this board's columns. */}
          <Link
            to="/settings/platform/sales-stages"
            className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-brand-bright transition-colors hover:text-brand-active"
          >
            <PiGearSix size={16} />
            Stage Settings
          </Link>
        </div>
      )}
      {dealKanbanView ? (
        <>
          <TabPanel<DealsView>
            idPrefix="deals-view"
            tabKey="table"
            activeKey={view}
            className="flex min-h-0 flex-1 flex-col"
          >
            <TableLinkStyleProvider variant="plain">
              <FBPageTableContainer className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
                {tableContent}
              </FBPageTableContainer>
            </TableLinkStyleProvider>
          </TabPanel>
          <TabPanel<DealsView>
            idPrefix="deals-view"
            tabKey="board"
            activeKey={view}
            className="flex min-h-0 flex-1 flex-col"
          >
            <DealsKanbanBoard />
          </TabPanel>
        </>
      ) : (
        <TableLinkStyleProvider variant="plain">
          <FBPageTableContainer className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
            {tableContent}
          </FBPageTableContainer>
        </TableLinkStyleProvider>
      )}
    </FBPage>
  )
}
